/* @sravni/react-design-system version 0.2.65 */
import { __assign, __rest, __read } from 'tslib';
import * as React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useStyles, safeInvoke } from '@sravni/react-utils';
import { EASINGS } from './_utils.js';
import modules_a1e6048f from '../styles/Transition.module.less.js';

var hasHeightValue = function (value) { return value != null && parseInt(value.toString(), 10) > 0; };
var variants = {
    exit: function (props) {
        return props.withAnimation
            ? __assign(__assign({}, (props.animateOpacity && {
                opacity: hasHeightValue(props.startingHeight) ? 1 : 0,
            })), { height: props.startingHeight, transition: {
                    height: { duration: 0.2, ease: EASINGS.ease },
                    opacity: { duration: 0.3, ease: EASINGS.ease },
                } }) : {};
    },
    enter: function (props) {
        return props.withAnimation
            ? __assign(__assign({}, (props.animateOpacity && {
                opacity: 1,
            })), { height: props.endingHeight, transition: {
                    height: {
                        duration: 0.3,
                        ease: EASINGS.ease,
                    },
                    opacity: {
                        duration: 0.4,
                        ease: EASINGS.ease,
                    },
                }, transitionEnd: { display: null } }) : {};
    },
};
var fadeConfig = {
    variants: variants,
    initial: 'exit',
    animate: 'enter',
    exit: 'exit',
};
var Collapse = React.forwardRef(function (props, ref) {
    var unmountOnExit = props.unmountOnExit, visible = props.visible, _a = props.enableAnimation, enableAnimation = _a === void 0 ? true : _a, _b = props.animateOpacity, animateOpacity = _b === void 0 ? true : _b, _c = props.startingHeight, startingHeight = _c === void 0 ? 0 : _c, _d = props.endingHeight, endingHeight = _d === void 0 ? 'auto' : _d, className = props.className, onAnimationComplete = props.onAnimationComplete, rest = __rest(props, ["unmountOnExit", "visible", "enableAnimation", "animateOpacity", "startingHeight", "endingHeight", "className", "onAnimationComplete"]);
    var cx = useStyles(modules_a1e6048f);
    var fromZeroHeight = startingHeight === 0;
    var _e = __read(React.useState(!!visible), 2), open = _e[0], setOpen = _e[1];
    var getInitialHidden = function () {
        // If it is open by default, no need to apply `aria-hidden`
        if (visible)
            return false;
        // If startingHeight > 0, then content is partially visible
        if (hasHeightValue(startingHeight))
            return false;
        // Else, the content is hidden
        return true;
    };
    var _f = __read(React.useState(function () {
        if (!fromZeroHeight)
            return true;
        var hidden = getInitialHidden();
        return hidden ? false : true;
    }), 2), display = _f[0], setDisplay = _f[1];
    var collapseClassnames = cx(className, 'collapseOverflow', {
        collapseVisible: display === true,
        collapseHidden: display === false,
    });
    var custom = {
        startingHeight: startingHeight,
        endingHeight: endingHeight,
        animateOpacity: animateOpacity,
        withAnimation: enableAnimation,
    };
    var ownProps = __assign(__assign({ ref: ref, 
        // @future: set parameter as `definition` when we remove `framer-motion`
        // v3 support
        onAnimationComplete: function (definition) {
            if (definition === 'exit' && !open && fromZeroHeight) {
                setDisplay(false);
            }
            safeInvoke(onAnimationComplete, definition);
        } }, rest), { variants: variants,
        custom: custom });
    React.useEffect(function () {
        if (visible) {
            setDisplay(true);
        }
        else {
            !enableAnimation && setDisplay(false);
        }
        setOpen(!!visible);
    }, [visible, enableAnimation]);
    if (unmountOnExit) {
        return (React.createElement(AnimatePresence, { initial: false, custom: custom }, visible && (React.createElement(motion.div, __assign({}, ownProps, { className: collapseClassnames, initial: 'exit', animate: 'enter', exit: 'exit' })))));
    }
    return React.createElement(motion.div, __assign({}, ownProps, { className: collapseClassnames, initial: false, animate: open ? 'enter' : 'exit' }));
});
Collapse.defaultProps = {
    animateOpacity: true,
    startingHeight: 0,
    endingHeight: 'auto',
    enableAnimation: true,
};
Collapse.displayName = 'Collapse';

export { Collapse, fadeConfig };
//# sourceMappingURL=Collapse.js.map
